import * as React from "react"
import { Col, Container, Modal, Row, Button, Table, Spinner } from "react-bootstrap"
import { Subscription } from "rxjs"
import FileSaver from "file-saver"
import { toast } from "react-toastify"
import ActionButton from "../ActionButton"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import PoolSelectionRow from "../../api/Randoms/RandomsModel/PoolSelectionRow"
import RandomSelectionRow from "../../api/Randoms/RandomsModel/RandomSelectionRow"
import RandomSelectionListGet from "../../api/Randoms/RandomSelectionListGet"
import RandomSelectionListCsvGet from "../../api/Randoms/RandomSelectionListCsvGet"
import RandomSelectionListPrintGet from "../../api/Randoms/RandomSelectionListPrintGet"
import RandomSelectionListPrintLettersGet from "../../api/Randoms/RandomSelectionListPrintLettersGet"

interface RandomSelectionListProps {
  show: boolean
  onHide: () => void
  poolId: number
  accountId?: number
  poolName: string
  poolSelection: PoolSelectionRow
}

interface RandomSelectionListState {
  data?: RandomSelectionRow[]
  loadingCsv: boolean
  loadingPrint: boolean
  loadingPrintLetter: boolean
}

export default class RandomSelectionList extends React.PureComponent<RandomSelectionListProps, RandomSelectionListState> {
  state: RandomSelectionListState = {
    loadingCsv: false,
    loadingPrint: false,
    loadingPrintLetter: false
  }
  listSubscription?: Subscription
  csvSubscription?: Subscription
  printSubscription?: Subscription
  printLetterSubscription?: Subscription

  componentDidMount() {
    this.listSubscription = RandomSelectionListGet(this.props.poolId, this.props.poolSelection.PoolSelectionID, this.props.accountId || 0).subscribe(
      (data: RandomSelectionRow[]) => {
        this.setState({ data: data.filter(row => row.ID !== "" || row.Name !== "" || row.TestFor !== "") })
      }
    )
  }

  componentWillUnmount() {
    this.listSubscription && this.listSubscription.unsubscribe()
    this.csvSubscription && this.csvSubscription.unsubscribe()
    this.printSubscription && this.printSubscription.unsubscribe()
    this.printLetterSubscription && this.printLetterSubscription.unsubscribe()
  }

  handleExportClick = () => {
    this.setState({ loadingCsv: true })
    this.csvSubscription = RandomSelectionListCsvGet(this.props.poolId, this.props.poolSelection.PoolSelectionID, this.props.poolName, this.props.accountId || 0).subscribe(
      (result: string) => result ? this.export(result) : toast.error("Data not found."),
      error => toast.error(error),
      () => this.setState({ loadingCsv: false })
    )
  }

  export = (csv: string) => {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName("RandomSelectionList", "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  handlePrintClick = () => {
    this.setState({ loadingPrint: true })
    this.printSubscription = RandomSelectionListPrintGet(this.props.poolId, this.props.poolSelection.PoolSelectionID, this.props.accountId || 0).subscribe(
      (data: string) => this.print(data),
      error => toast.error(error),
      () => this.setState({ loadingPrint: false })
    )
  }

  print = (base64pdf: string) => {
    const pdfWindow = window.open("")
    if (pdfWindow) {
      pdfWindow.document.write("<iframe width='100%' height='100%' src='data:application/pdf;base64, " + encodeURI(base64pdf) + "'></iframe>")
    }
  }

  handlePrintLettersClick = () => {
    this.setState({ loadingPrintLetter: true })
    this.printLetterSubscription = RandomSelectionListPrintLettersGet(this.props.poolId, this.props.poolSelection.PoolSelectionID, this.props.accountId || 0).subscribe(
      (data: string) => this.print(data),
      error => toast.error(error),
      () => this.setState({ loadingPrintLetter: false })
    )
  }

  render() {
    return (
      <Modal backdrop="static" show={this.props.show} onHide={this.props.onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>RANDOM SELECTION LIST</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container className="grid-container">
            <Row>
              <Col>
                <h5>{this.props.poolName}</h5>{this.props.poolSelection.TimePeriod}
              </Col>
              <Col>
                <Row>
                  <Col className="text-right">
                    <ActionButton variant="secondary" size="sm" onClick={this.handleExportClick} className="mr-2" disabled={!this.state.data?.length} spin={this.state.loadingCsv} disableOnSpin>EXPORT</ActionButton>
                    <ActionButton variant="secondary" size="sm" onClick={this.handlePrintClick} className="mr-2" disabled={!this.state.data?.length} spin={this.state.loadingPrint} disableOnSpin>PRINT</ActionButton>
                  </Col>
                </Row>
                <Row>
                  <Col className="text-right my-2">
                    <ActionButton variant="secondary" size="sm" onClick={this.handlePrintLettersClick} className="mr-2" disabled={!this.state.data?.length} spin={this.state.loadingPrintLetter} disableOnSpin>PRINT NOTIFICATION LETTERS</ActionButton>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col>
                <Table responsive className="table-sm dark">
                  <thead>
                    <tr className="text-normal">
                      <th>COMPANY</th>
                      <th>PRIMARY ID</th>
                      <th>NAME</th>
                      <th>TEST FOR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!this.state.data && <tr><td className="text-center" colSpan={4}><Spinner animation="border" variant="secondary" /></td></tr>}
                    {this.state.data && this.state.data.length === 0 && <tr><td colSpan={4}>NO SELECTIONS FOR THIS ACCOUNT.</td></tr>}
                    {this.state.data && this.state.data.length > 0 && this.state.data.map((row: RandomSelectionRow, i: number) =>
                      <tr key={i}>
                        <td>{row.Company}</td>
                        <td>{row.ID}</td>
                        <td>{row.Name}</td>
                        <td>{row.TestFor}</td>
                      </tr>)}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.props.onHide}>CLOSE</Button>
        </Modal.Footer>
      </Modal>
    )
  }
}
